import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Tag,
  message,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { createMeterReadingRecord } from "../../api/host";
import HostSelect from "../host/HostSelect";

const genKey = () =>
  window.crypto?.randomUUID?.() ??
  `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const setNumericKeyboard = (el, mode = "numeric") =>
  el?.setAttribute?.("inputmode", mode);
const css = `
.mr-dhead{box-sizing:content-box;height:44px;display:flex;align-items:center;justify-content:space-between;
  padding:env(safe-area-inset-top,0px) 6px 0 14px;border-bottom:1px solid #f0f0f0;}
.mr-dhead-title{display:flex;align-items:center;gap:8px;font-size:15px;font-weight:600;color:#313740;}
.mr-field .ant-form-item-label{transition:flex-basis .2s ease;}
.mr-field .ant-form-item-label>label{width:100%;font-size:13px;color:#64748b;transition:opacity .15s;}
.mr-field.is-filled .ant-form-item-label>label{opacity:0;}
.mr-unit-wrap{position:relative;}
.mr-unit-wrap .ant-input-number-input{padding-right:42px;}
.mr-unit-text{position:absolute;right:11px;top:50%;transform:translateY(-50%);
  font-size:12px;color:#94a3b8;pointer-events:none;}
`;

const hasValue = (v) => v !== undefined && v !== null && v !== "";

// 带单位后缀的数字输入，可直接作为 Form.Item 的子元素
const UnitNumber = forwardRef(function UnitNumber({ unit, ...props }, ref) {
  return (
    <div className="mr-unit-wrap">
      <InputNumber
        ref={ref}
        placeholder="0.00"
        min={0}
        controls={false}
        inputMode="decimal"
        style={{ width: "100%" }}
        {...props}
      />
      <span className="mr-unit-text">{unit}</span>
    </div>
  );
});

// 标题在左、输入在右；filled 时标题收起，输入占满整行
function Field({ name, label, rules, collapsed = false, children }) {
  return (
    <Form.Item
      name={name}
      label={label}
      rules={rules}
      className={`mr-field${collapsed ? " is-filled" : ""}`}
      labelCol={{ flex: collapsed ? "0px" : "64px" }}
      wrapperCol={{ flex: "auto" }}
      style={{ marginBottom: 10 }}
    >
      {children}
    </Form.Item>
  );
}
// ─── 表单本体：每次打开都会重新挂载，状态天然干净 ─────────────────────────────
const MeterReadingForm = forwardRef(function MeterReadingForm(
  { host, onSuccess, onClose, onBusyChange },
  ref,
) {
  const [form] = Form.useForm();
  const [focused, setFocused] = useState(null);
  const refElectric = useRef(null);
  const refDate = useRef(null);
  const refRemark = useRef(null);
  const [dateOpen, setDateOpen] = useState(false);
  const [initialHost] = useState(host); // 打开瞬间的快照，关闭动画期间外部清空 host 也不受影响
  const [initialValues] = useState(() => ({
    host: host?._id,
    readingTime: dayjs(),
  }));
  const [idempotencyKey] = useState(genKey); // 同一次打开内重试共用同一个 key
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const refHost = useRef(null);
  const refWater = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => (initialHost ? refWater : refHost).current?.focus(),
  }));

  const setBusy = (b) => {
    submittingRef.current = b;
    setSubmitting(b);
    onBusyChange?.(b);
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return; // 同步锁，防连点和弱网重复提交

    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // 校验失败，antd 自己标红
    }

    setBusy(true);
    let created = null;
    try {
      const res = await createMeterReadingRecord({
        host: initialHost?._id ?? values.host,
        waterReading: values.waterReading,
        electricReading: values.electricReading,
        readingTime: values.readingTime.toDate(),
        remark: values.remark,
        idempotencyKey, // 后端若支持幂等可据此去重，不支持则忽略
      });
      if (res.status === 201) {
        created = res;
      } else {
        message.error("提交失败");
      }
    } catch (e) {
      if (e?.response) {
        message.error(e.response.data?.message || "提交失败，请检查后重试");
      } else {
        message.warning("网络不稳，请先确认是否已提交，再决定是否重试");
      }
    } finally {
      setBusy(false); // 先解锁，再关闭，避免外层的关闭保护拦住自己
    }

    if (created) {
      message.success("提交成功");
      if (created.data?.warnings?.length) {
        message.warning(
          `有 ${created.data.warnings.length} 张水电账单未更新，请核对读数`,
        );
      }
      onSuccess?.(created.data);
      onClose?.();
    }
  };
  // 操作顺序：带 host 时没有房间号这一项
  const refs = {
    host: refHost,
    water: refWater,
    electric: refElectric,
    date: refDate,
    remark: refRemark,
  };
  const order = initialHost
    ? ["water", "electric", "date", "remark"]
    : ["host", "water", "electric", "date", "remark"];
  const focusNext = (from) => {
    const next = order[order.indexOf(from) + 1];
    if (next) refs[next].current?.focus();
  };
  const focusProps = (name) => ({
    onFocus: () => setFocused(name),
    onBlur: () => setFocused((cur) => (cur === name ? null : cur)),
  });
  return (
    <>
      <style>{css}</style>

      <div className="mr-dhead">
        <div className="mr-dhead-title">
          {initialHost?.roomNumber != null && (
            <Tag
              color="blue"
              style={{
                margin: 0,
                borderRadius: 5,
                fontWeight: 600,
                fontSize: 14,
              }}
            >
              {initialHost.roomNumber}
            </Tag>
          )}
          <span>{initialHost ? "抄表" : "新增抄表"}</span>
        </div>
        <Button
          type="text"
          aria-label="关闭"
          icon={<CloseOutlined />}
          disabled={submitting}
          onClick={onClose}
          style={{ width: 36, height: 36, color: "#64748b" }}
        />
      </div>

      <div style={{ padding: "12px 14px 14px" }}>
        <Form
          form={form}
          labelAlign="left"
          colon={false}
          requiredMark={false}
          initialValues={initialValues}
          disabled={submitting}
        >
          {!initialHost && (
            <Field
              name="host"
              label="房间号"
              collapsed={focused === "host"}
              rules={[{ required: true, message: "请选择房间" }]}
            >
              <HostSelect
                ref={refHost}
                placeholder="输入房号数字..."
                filters={{ valid: true }}
                onFocus={() => setFocused("host")}
                onBlur={focusProps("host").onBlur}
                onChange={(v) => v && focusNext("host")}
              />
            </Field>
          )}

          <Field
            name="waterReading"
            label="水表读数"
            collapsed={focused === "water"}
            rules={[{ required: true, message: "请输入水表读数" }]}
          >
            <UnitNumber
              ref={refWater}
              unit="m³"
              enterKeyHint="next"
              onPressEnter={() => focusNext("water")}
              {...focusProps("water")}
            />
          </Field>

          <Field
            name="electricReading"
            label="电表读数"
            collapsed={focused === "electric"}
            rules={[{ required: true, message: "请输入电表读数" }]}
          >
            <UnitNumber
              ref={refElectric}
              unit="kWh"
              enterKeyHint="next"
              onPressEnter={(e) =>
                e.ctrlKey || e.metaKey ? handleSubmit() : focusNext("electric")
              }
              {...focusProps("electric")}
            />
          </Field>

          <Field
            name="readingTime"
            label="抄表时间"
            collapsed={focused === "date"}
            rules={[{ required: true, message: "请选择抄表时间" }]}
          >
            <DatePicker
              ref={refDate}
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              inputReadOnly
              allowClear={false}
              open={dateOpen}
              onOpenChange={(o) => {
                if (!o) setDateOpen(false);
              }}
              onClick={() => setDateOpen(true)}
              onKeyDown={(e) => {
                if (!dateOpen && (e.key === "Enter" || e.key === "ArrowDown")) {
                  e.preventDefault();
                  setDateOpen(true);
                }
              }}
              onChange={() => focusNext("date")}
              {...focusProps("date")}
            />
          </Field>

          <Field name="remark" label="备注" collapsed={focused === "remark"}>
            <Input.TextArea
              ref={refRemark}
              autoSize={{ minRows: 1, maxRows: 3 }}
              placeholder="选填"
              {...focusProps("remark")}
            />
          </Field>

          <Button
            block
            type="primary"
            size="large"
            loading={submitting}
            onClick={handleSubmit}
            style={{ marginTop: 2 }}
          >
            确认添加
          </Button>
        </Form>
      </div>
    </>
  );
});

// ─── 对外组件 ─────────────────────────────────────────────────────────────────
/**
 * @param {boolean} open
 * @param {{ _id: string, roomNumber?: string|number } | null} host  传入则预填并锁定房间，不传则可自选
 * @param {() => void} onClose
 * @param {(record: object) => void} onSuccess  提交成功后触发，组件随后自行调用 onClose
 */
export default function MeterReadingDrawer({
  open,
  host = null,
  onClose,
  onSuccess,
}) {
  const formRef = useRef(null);
  const busyRef = useRef(false);
  const [busy, setBusy] = useState(false);

  // 每次从关闭变为打开，session +1，表单随 key 重新挂载
  const [prevOpen, setPrevOpen] = useState(open);
  const [session, setSession] = useState(0);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setSession((s) => s + 1);
  }

  const handleBusyChange = (b) => {
    busyRef.current = b;
    setBusy(b);
  };

  // 提交中不允许通过遮罩、ESC、关闭按钮关掉
  const handleClose = () => {
    if (busyRef.current) return;
    onClose?.();
  };

  return (
    <Drawer
      open={open}
      placement="top"
      height="auto"
      title={null}
      closable={false}
      styles={{
        body: { padding: 0 },
        wrapper: { borderRadius: "0 0 14px 14px", overflow: "hidden" },
      }}
      onClose={handleClose}
      maskClosable={!busy}
      keyboard={!busy}
      afterOpenChange={(visible) => {
        if (visible) formRef.current?.focus();
      }}
    >
      <MeterReadingForm
        key={session}
        ref={formRef}
        host={host}
        onSuccess={onSuccess}
        onClose={handleClose}
        onBusyChange={handleBusyChange}
      />
    </Drawer>
  );
}
